import React, { useState } from 'react';
import { X, Upload, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { QRCodeSVG } from 'qrcode.react';

export const CardCustomizationModal = ({ isOpen, onClose, onConfirm, cardType, productImage }) => {
  const [formData, setFormData] = useState({
    displayName: '',
    logo: null,
    logoPreview: null,
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          logo: file,
          logoPreview: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-[2rem] max-w-6xl w-full p-8 relative my-8">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 z-10"
        >
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-3xl font-bold text-navy mb-2">
          Customize Your {cardType} Card
        </h2>
        <p className="text-gray-600 mb-8">
          Fill in your details to see a live preview of your card
        </p>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Side - Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">
                  Name to Display on Card
                </label>
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  placeholder="Your Business Name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">
                  Logo
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-navy transition-colors">
                  {formData.logoPreview ? (
                    <div className="space-y-3">
                      <img
                        src={formData.logoPreview}
                        alt="Logo preview"
                        className="w-24 h-24 object-contain mx-auto"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, logo: null, logoPreview: null })}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove Logo
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Click to upload logo</p>
                      <p className="text-xs text-gray-400">PNG, JPG up to 5MB</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-navy text-white hover:bg-navy/90 rounded-full py-6 text-base font-semibold"
              >
                Confirm & Continue
              </Button>
            </form>
          </div>

          {/* Right Side - Preview */}
          <div className="flex items-center justify-center">
            <div className="w-full max-w-sm">
              <p className="text-sm font-semibold text-gray-600 mb-4 text-center">
                Card Preview
              </p>
              
              {/* Product Image Preview */}
              {productImage ? (
                <div className="bg-gray-50 rounded-2xl p-4 shadow-lg border border-gray-200">
                  <img
                    src={productImage}
                    alt={cardType}
                    className="w-full h-auto rounded-lg object-contain max-h-96"
                  />
                  
                  {/* Overlay with customization info */}
                  <div className="mt-4 p-4 bg-white rounded-lg border border-gray-100">
                    <h4 className="font-semibold text-navy text-sm mb-2">Your Customization:</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">Business Name:</span>
                        <span className="font-medium text-navy">
                          {formData.displayName || 'Not entered yet'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">Logo:</span>
                        {formData.logoPreview ? (
                          <img src={formData.logoPreview} alt="Logo" className="w-8 h-8 object-contain" />
                        ) : (
                          <span className="text-gray-400 italic">Not uploaded</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Fallback Preview - Review Card Style */
                <div 
                  className="bg-white rounded-2xl p-6 shadow-2xl border border-gray-200 relative"
                  style={{ aspectRatio: '5.4/8.56', maxWidth: '280px', margin: '0 auto' }}
                >
                  <div className="h-full flex flex-col items-center justify-between">
                    {/* Top spacing */}
                    <div className="flex-shrink-0 h-2"></div>

                    {/* Logo */}
                    {formData.logoPreview && (
                      <div className="flex-shrink-0 mb-2">
                        <img
                          src={formData.logoPreview}
                          alt="Logo"
                          className="w-16 h-16 object-contain"
                        />
                      </div>
                    )}

                    {/* Business Name */}
                    {formData.displayName && (
                      <div className="text-center flex-shrink-0 mb-3">
                        <h3 className="text-sm font-bold text-navy">
                          {formData.displayName}
                        </h3>
                      </div>
                    )}

                    {/* QR Code - Static */}
                    <div className="flex-shrink-0 bg-white p-2 rounded-lg">
                      <QRCodeSVG
                        value="https://smartconnections.club"
                        size={120}
                        level="H"
                        includeMargin={false}
                      />
                    </div>

                    {/* Stars */}
                    <div className="flex justify-center gap-1 my-2 flex-shrink-0">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>

                    {/* Call to Action Text */}
                    <div className="text-center flex-shrink-0 mb-2">
                      <p className="text-xs text-gray-700 font-medium">
                        Tap or Scan to review us on
                      </p>
                    </div>

                    {/* Platform Logo */}
                    <div className="flex justify-center mb-2 flex-shrink-0">
                      {cardType === 'Google Review' ? (
                        <svg className="h-6" viewBox="0 0 272 92" xmlns="http://www.w3.org/2000/svg">
                          <path d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18C71.25 34.32 81.24 25 93.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44S80.99 39.2 80.99 47.18c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z" fill="#EA4335"/>
                          <path d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z" fill="#FBBC05"/>
                          <path d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z" fill="#4285F4"/>
                          <path d="M225 3v65h-9.5V3h9.5z" fill="#34A853"/>
                          <path d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z" fill="#EA4335"/>
                          <path d="M35.29 41.41V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91.36 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49.01z" fill="#4285F4"/>
                        </svg>
                      ) : (
                        <div className="text-xl font-bold text-green-600">
                          TripAdvisor
                        </div>
                      )}
                    </div>

                    {/* NFC and QR Enabled */}
                    <div className="text-center flex-shrink-0 mb-1">
                      <p className="text-[10px] text-gray-500">
                        NFC and QR Enabled
                      </p>
                    </div>

                    {/* Powered by Smart Connections Logo */}
                    <div className="text-center flex-shrink-0 mt-auto">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_review-cards-1/artifacts/ip2pgeii_JPEG-02-removebg-preview.png" 
                        alt="Smart Connections" 
                        className="h-8 mx-auto opacity-70"
                      />
                      <p className="text-[8px] text-gray-400 mt-1">Powered by Smart Connections</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
